import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from './Settings';

// ───────────────────────────────────────────────────────────────────────────────
// Settings → My Account  (Admin only)
//   Details          plan, seats, duration, renewal date, add-on modules, Renew / Upgrade
//   Billing History  every payment recorded for the firm, with a printable invoice
// Renew / Upgrade open a checkout (price quote, promo code, order). Until a payment gateway is
// connected the order is saved as pending and the platform team marks it paid in Super Admin.
// All data comes from /api/account.
// ───────────────────────────────────────────────────────────────────────────────

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-') : '—');
const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS = {
  active:    { label: 'Active',    fg: '#15803d', bg: '#dcfce7' },
  trial:     { label: 'Free trial', fg: '#b45309', bg: '#fef3c7' },
  expired:   { label: 'Expired',   fg: '#b91c1c', bg: '#fee2e2' },
  suspended: { label: 'Suspended', fg: '#b91c1c', bg: '#fee2e2' },
};

export default function AccountSettings({ token, darkMode, T }) {
  const { showToast } = useToast();
  const [tab, setTab] = useState('details');
  const [data, setData] = useState(null);
  const [bills, setBills] = useState(null);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(null); // 'renew' | 'upgrade'

  const api = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${BASE}/api/account${path}`, {
      ...opts,
      headers: { Authorization: `Bearer ${token}`, ...(opts.body ? { 'Content-Type': 'application/json' } : {}) },
    });
    return res;
  }, [token]);

  const loadOverview = useCallback(async () => {
    try {
      const res = await api('/overview');
      const body = await res.json();
      if (!res.ok) throw new Error(body.message || 'Could not load your account.');
      setData(body); setError('');
    } catch (e) { setError(e.message); }
  }, [api]);

  const loadBills = useCallback(async () => {
    try {
      const res = await api('/billing');
      const body = await res.json();
      if (!res.ok) throw new Error(body.message || 'Could not load billing history.');
      setBills(body);
    } catch (e) { showToast(e.message); setBills([]); }
  }, [api, showToast]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { if (tab === 'billing' && bills === null) loadBills(); }, [tab, bills, loadBills]);

  const cancelPending = async () => {
    if (!data?.pending_order) return;
    const res = await api(`/orders/${data.pending_order.id}/cancel`, { method: 'POST' });
    const b = await res.json().catch(() => ({}));
    showToast(b.message || (res.ok ? 'Order cancelled.' : 'Could not cancel the order.'), res.ok ? 'success' : 'error');
    loadOverview();
  };

  const openInvoice = async (id) => {
    const win = window.open('', '_blank');           // opened first so the browser does not block it
    try {
      const res = await api(`/billing/${id}/invoice`);
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not open the invoice.');
      const html = await res.text();
      if (win) { win.document.open(); win.document.write(html); win.document.close(); }
    } catch (e) { if (win) win.close(); showToast(e.message); }
  };

  const card = { background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 14 };
  const label = { fontSize: 13, color: T.text3 };
  const value = { fontSize: 13.5, color: T.text, fontWeight: 500 };

  if (error) return <div style={{ padding: '40px 0', textAlign: 'center', color: T.text3, fontSize: 13 }}>{error}</div>;
  if (!data) return <div style={{ padding: '40px 0', textAlign: 'center', color: T.text3, fontSize: 13 }}>Loading your account…</div>;

  const sub = data.subscription;
  const st = STATUS[sub.status] || { label: sub.status, fg: T.text2, bg: T.bg3 };
  const seatPct = sub.users_limit ? Math.min(100, Math.round((sub.users_used / sub.users_limit) * 100)) : 0;
  const expired = sub.status === 'expired' || (sub.days_left !== null && sub.days_left < 0);
  const soon = !expired && sub.days_left !== null && sub.days_left <= 15;
  const canUpgrade = sub.status === 'active' && !expired;

  const rows = [
    ['Name', data.contact.name],
    ['Firm', data.firm.name],
    ['Mobile No.', data.contact.phone],
    ['Email', data.contact.email],
    ['Subscription Status', <span style={{ padding: '2px 10px', borderRadius: 99, background: st.bg, color: st.fg, fontSize: 12, fontWeight: 700 }}>{st.label}</span>],
    ['Plan', (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span>{sub.plan_label}{sub.users_limit ? ` · ${sub.users_limit} users` : ''}</span>
        <button onClick={() => setDialog('renew')} style={btn('#2563eb', '#fff')}>{sub.is_trial || expired ? 'Buy plan' : 'Renew'}</button>
        <button onClick={() => canUpgrade && setDialog('upgrade')} disabled={!canUpgrade} title={canUpgrade ? 'Add users to your plan' : 'Available once you have an active paid plan'}
          style={{ ...btn('#2563eb', '#fff'), opacity: canUpgrade ? 1 : 0.45, cursor: canUpgrade ? 'pointer' : 'not-allowed' }}>Upgrade</button>
      </span>
    )],
    ['Users in use', sub.users_limit ? (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
        <span>{sub.users_used} of {sub.users_limit}</span>
        <span style={{ width: 120, height: 6, borderRadius: 99, background: T.border, overflow: 'hidden' }}>
          <span style={{ display: 'block', width: `${seatPct}%`, height: '100%', background: seatPct >= 100 ? '#dc2626' : '#3b82f6' }} />
        </span>
      </span>
    ) : `${sub.users_used} active`],
    ['Subscription Duration', sub.months ? `${sub.months} months` : '—'],
    [sub.is_trial ? 'Trial Ends On' : 'Renewal Date', fmtDate(sub.renews_on)],
  ];

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: 0 }}>My Account</h3>
        <p style={{ fontSize: 12.5, color: T.text3, margin: '3px 0 0' }}>Your plan, renewal date and billing — visible to the firm Admin only.</p>
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${T.border}`, marginBottom: 20 }}>
        {[['details', 'Details'], ['billing', 'Billing History']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13.5, fontWeight: 600,
            color: tab === k ? '#2563eb' : T.text3, borderBottom: `2px solid ${tab === k ? '#2563eb' : 'transparent'}`, marginBottom: -1,
          }}>{l}</button>
        ))}
      </div>

      {tab === 'details' && (
        <>
          {(expired || soon) && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '12px 16px', marginBottom: 16, borderRadius: 12,
              background: expired ? '#fef2f2' : '#fffbeb', border: `1px solid ${expired ? '#fecaca' : '#fde68a'}`, color: expired ? '#991b1b' : '#92400e', fontSize: 13 }}>
              <span>{expired ? `Your ${sub.is_trial ? 'trial' : 'subscription'} has ended.` : `Your ${sub.is_trial ? 'trial' : 'subscription'} ends in ${sub.days_left} day${sub.days_left === 1 ? '' : 's'}.`} {sub.is_trial ? 'Choose a plan to keep going.' : 'Renew to avoid any interruption.'}</span>
              <button onClick={() => setDialog('renew')} style={btn('#2563eb', '#fff')}>{sub.is_trial ? 'Choose a plan' : 'Renew now'}</button>
            </div>
          )}

          {data.pending_order && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '10px 16px', marginBottom: 16, borderRadius: 12, background: darkMode ? 'rgba(59,130,246,0.1)' : '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', fontSize: 13 }}>
              <span>Order <b>{data.pending_order.order_no}</b> · {data.pending_order.description} · <b>{inr(data.pending_order.total)}</b> is awaiting payment.</span>
              <button onClick={cancelPending} style={btn('transparent', '#1d4ed8')}>Cancel order</button>
            </div>
          )}

          <div style={{ ...card, padding: '10px 28px' }}>
            {rows.map(([k, v], i) => (
              <div key={k} style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, 240px) 1fr', gap: 16, padding: '12px 0', alignItems: 'center', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
                <span style={label}>{k}</span>
                <span style={value}>{v || '—'}</span>
              </div>
            ))}
          </div>

          {data.modules.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 10px' }}>Add-on modules</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 12 }}>
                {data.modules.map(m => {
                  const on = m.status === 'active' || m.status === 'trial';
                  return (
                    <div key={m.name} style={{ ...card, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{m.name}</div>
                        <div style={{ fontSize: 11.5, color: T.text3 }}>{on ? (m.expires_at ? `Until ${fmtDate(m.expires_at)}` : 'No expiry') : 'Not subscribed'}</div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 99, background: on ? '#dcfce7' : T.bg3, color: on ? '#15803d' : T.text3 }}>
                        {m.status === 'trial' ? 'Trial' : on ? 'Active' : 'Off'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'billing' && (
        <div style={{ ...card, overflow: 'hidden' }}>
          {bills === null ? (
            <div style={{ padding: 40, textAlign: 'center', color: T.text3, fontSize: 13 }}>Loading…</div>
          ) : bills.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: T.text3, fontSize: 13 }}>No payments recorded yet. They will appear here after your first purchase.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
                <thead>
                  <tr>{['Date', 'Item', 'Amount', 'Status', 'Action'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '14px 20px', fontSize: 11.5, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${T.border}` }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {bills.map(b => (
                    <tr key={b.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <td style={{ padding: '14px 20px', fontSize: 13, color: T.text }}>{fmtDate(b.paid_on || b.created_at)}</td>
                      <td style={{ padding: '14px 20px', fontSize: 13, color: T.text }}>
                        {b.description}<div style={{ fontSize: 11, color: T.text3 }}>{b.invoice_no}</div>
                      </td>
                      <td style={{ padding: '14px 20px', fontSize: 13, color: T.text }}>{inr(b.total)}</td>
                      <td style={{ padding: '14px 20px', fontSize: 12.5, color: b.status === 'paid' ? '#15803d' : b.status === 'pending' ? '#b45309' : T.text3, fontWeight: 600, textTransform: 'capitalize' }}>{b.status}</td>
                      <td style={{ padding: '14px 20px' }}>
                        <button onClick={() => openInvoice(b.id)} style={btn(darkMode ? 'rgba(59,130,246,0.15)' : '#dbeafe', '#1d4ed8')}>Download Invoice</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {dialog && (
        <CheckoutDialog
          kind={dialog} T={T} darkMode={darkMode} api={api}
          onClose={() => { setDialog(null); loadOverview(); }}
          onDone={() => { setDialog(null); loadOverview(); setBills(null); }}
        />
      )}
    </div>
  );
}

const btn = (bg, fg) => ({ padding: '5px 14px', borderRadius: 8, border: 'none', background: bg, color: fg, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' });

// ── Renew / Upgrade checkout ─────────────────────────────────────────────────────
// Every amount shown here comes from POST /api/account/quote; the order is priced again on the
// server, so nothing on this screen can change what is charged.
const PAY_METHODS = [['upi', 'UPI'], ['card', 'Credit/Debit Card'], ['netbanking', 'Net Banking']];

function CheckoutDialog({ kind, T, darkMode, api, onClose, onDone }) {
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [months, setMonths] = useState(null);
  const [users, setUsers] = useState(null);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState('');           // the code currently applied
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState('');
  const [quoting, setQuoting] = useState(false);
  const [company, setCompany] = useState('');
  const [gstin, setGstin] = useState('');
  const [payMethod, setPayMethod] = useState('upi');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [placed, setPlaced] = useState(null);       // { order, payment }

  useEffect(() => {
    (async () => {
      try {
        const res = await api('/checkout');
        const b = await res.json();
        if (!res.ok) throw new Error(b.message || 'Could not open checkout.');
        setInfo(b);
        setCompany(b.billing.company_name || '');
        setGstin(b.billing.gstin || '');
        setMonths(b.pricing.durations.includes(12) ? 12 : b.pricing.durations[0]);
        const cur = b.current.users || 0;
        setUsers(kind === 'upgrade'
          ? Math.min(b.pricing.max_users, cur + 1)
          : Math.max(cur, b.current.active_users, b.pricing.min_users));
      } catch (e) { setLoadError(e.message); }
    })();
  }, [api, kind]);

  // re-price whenever the selection changes (debounced so the slider does not flood the server)
  useEffect(() => {
    if (!info || users == null) return undefined;
    let live = true;
    const t = setTimeout(async () => {
      setQuoting(true);
      try {
        const res = await api('/quote', { method: 'POST', body: JSON.stringify({ kind, users, months, promo_code: promo || undefined }) });
        const b = await res.json();
        if (!live) return;
        if (!res.ok) { setQuote(null); setQuoteError(b.message || 'Could not price this selection.'); }
        else { setQuote(b); setQuoteError(''); }
      } catch { if (live) setQuoteError('Could not price this selection.'); }
      finally { if (live) setQuoting(false); }
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [api, info, kind, users, months, promo]);

  const pay = async () => {
    setFormError('');
    if (!company.trim()) { setFormError('Company name is required.'); return; }
    if (gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(gstin.trim().toUpperCase())) { setFormError('Enter a valid 15-character GSTIN, or leave it blank.'); return; }
    setBusy(true);
    try {
      const res = await api('/orders', { method: 'POST', body: JSON.stringify({ kind, users, months, promo_code: promo || undefined, company_name: company, gstin, pay_method: payMethod }) });
      const b = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(b.message || 'Could not place the order.');
      if (b.payment && b.payment.mode === 'redirect' && b.payment.url) { window.location.assign(b.payment.url); return; }
      setPlaced(b);
    } catch (e) { setFormError(e.message); } finally { setBusy(false); }
  };

  const input = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.text, fontSize: 13.5, outline: 'none' };
  const lab = { display: 'block', fontSize: 12.5, fontWeight: 600, color: T.text, marginBottom: 6 };
  const line = (l, v, strong) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '4px 0', color: strong ? T.text : T.text3, fontWeight: strong ? 700 : 400 }}>
      <span>{l}</span><span style={{ color: T.text, fontWeight: strong ? 700 : 500, whiteSpace: 'nowrap' }}>{v}</span>
    </div>
  );
  const title = kind === 'renew' ? 'Renew' : 'Upgrade';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 16 }} onMouseDown={busy ? undefined : onClose}>
      <div onMouseDown={e => e.stopPropagation()} style={{ background: T.bg2, borderRadius: 18, width: '100%', maxWidth: 560, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(15,31,69,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius: '18px 18px 0 0', flexShrink: 0 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#fff' }}>{title}</h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>{kind === 'renew' ? 'Extend your subscription' : 'Add users to your current plan'}</p>
          </div>
          <button onClick={onClose} disabled={busy} aria-label="Close" style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.12)', color: '#fff', cursor: 'pointer', fontSize: 16 }}>×</button>
        </div>

        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
          {loadError && <p style={{ margin: 0, fontSize: 13, color: '#dc2626' }}>{loadError}</p>}
          {!loadError && !info && <p style={{ margin: 0, fontSize: 13, color: T.text3 }}>Loading…</p>}

          {placed && (
            <div style={{ textAlign: 'center', padding: '12px 4px' }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#dcfce7', color: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: 24, fontWeight: 700 }}>✓</div>
              <h4 style={{ margin: '0 0 6px', fontSize: 15, color: T.text }}>Order {placed.order.order_no} created</h4>
              <p style={{ margin: '0 0 4px', fontSize: 13, color: T.text2 }}>{placed.order.description}</p>
              <p style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: T.text }}>{inr(placed.order.total)}</p>
              <p style={{ margin: 0, fontSize: 12.5, color: T.text3, lineHeight: 1.6 }}>{placed.payment.message}</p>
            </div>
          )}

          {info && !placed && (
            <div style={{ display: 'grid', gap: 18 }}>
              {kind === 'renew' ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={lab}>Duration <span style={{ color: '#dc2626' }}>*</span></label>
                    <select value={months || ''} onChange={e => setMonths(Number(e.target.value))} style={input}>
                      {info.pricing.durations.map(m => {
                        const off = Number(info.pricing.duration_discounts?.[m] || 0);
                        return <option key={m} value={m}>{m} Months{off ? ` (save ${off}%)` : ''}</option>;
                      })}
                    </select>
                  </div>
                  <div>
                    <label style={lab}>Users</label>
                    <input type="number" min={Math.max(info.pricing.min_users, info.current.active_users)} max={info.pricing.max_users}
                      value={users ?? ''} onChange={e => setUsers(e.target.value === '' ? '' : Number(e.target.value))} style={input} />
                    <p style={{ margin: '5px 0 0', fontSize: 11.5, color: T.text3 }}>{info.current.active_users} active user{info.current.active_users === 1 ? '' : 's'} today</p>
                  </div>
                </div>
              ) : (
                <div>
                  <label style={{ ...lab, marginBottom: 2 }}>Users</label>
                  <p style={{ margin: '0 0 10px', fontSize: 12, color: T.text3 }}>Select the total number of users you need (you have {info.current.users} today).</p>
                  <p style={{ margin: '0 0 6px', textAlign: 'center', fontSize: 15, fontWeight: 700, color: '#2563eb' }}>{users} users</p>
                  <input type="range" min={info.current.users + 1} max={info.pricing.max_users} value={users || info.current.users + 1}
                    onChange={e => setUsers(Number(e.target.value))} style={{ width: '100%', accentColor: '#2563eb' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.text3 }}><span>{info.current.users + 1}</span><span>{info.pricing.max_users}</span></div>
                </div>
              )}

              {/* promo code */}
              <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden' }}>
                <button onClick={() => setPromoOpen(o => !o)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 16px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: darkMode ? 'rgba(59,130,246,0.1)' : '#eff6ff', color: '#2563eb' }}>
                  <span>{promo && quote?.promo ? `Promo code ${quote.promo.code} applied` : 'Have a promo code?'}</span>
                  <span style={{ transform: promoOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>⌄</span>
                </button>
                {promoOpen && (
                  <div style={{ padding: 14 }}>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <input value={promoInput} onChange={e => setPromoInput(e.target.value.toUpperCase())} placeholder="Promo code" style={{ ...input, flex: 1 }}
                        onKeyDown={e => { if (e.key === 'Enter') setPromo(promoInput.trim()); }} />
                      {promo
                        ? <button onClick={() => { setPromo(''); setPromoInput(''); }} style={{ ...btn(T.bg3, T.text2), padding: '0 18px' }}>Remove</button>
                        : <button onClick={() => setPromo(promoInput.trim())} disabled={!promoInput.trim()} style={{ ...btn('#dbeafe', '#1d4ed8'), padding: '0 22px' }}>Apply</button>}
                    </div>
                    {promo && quote?.promo_error && <p style={{ margin: '8px 0 0', fontSize: 12, color: '#dc2626' }}>{quote.promo_error}</p>}
                    {promo && quote?.promo && <p style={{ margin: '8px 0 0', fontSize: 12, color: '#15803d' }}>You save {inr(quote.discount)}{quote.promo.description ? ` — ${quote.promo.description}` : ''}</p>}
                  </div>
                )}
              </div>

              {/* price summary */}
              <div style={{ padding: '6px 2px', opacity: quoting ? 0.55 : 1, transition: 'opacity 0.15s' }}>
                {quoteError ? <p style={{ margin: 0, fontSize: 13, color: '#dc2626' }}>{quoteError}</p> : quote && (
                  <>
                    {line(quote.description, inr(quote.base_amount))}
                    {quote.discount > 0 && line(`Promo ${quote.promo?.code || ''}`, `− ${inr(quote.discount)}`)}
                    {line(`GST (${quote.gst_percent}%)`, inr(quote.gst_amount))}
                    <div style={{ height: 1, background: T.border, margin: '6px 0' }} />
                    {line('Final Amount', inr(quote.total), true)}
                  </>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div><label style={lab}>Company Name <span style={{ color: '#dc2626' }}>*</span></label><input value={company} onChange={e => setCompany(e.target.value)} maxLength={200} style={input} /></div>
                <div><label style={lab}>GSTIN</label><input value={gstin} onChange={e => setGstin(e.target.value.toUpperCase())} maxLength={15} placeholder="Optional" style={input} /></div>
              </div>

              <div>
                <label style={lab}>Pay Using</label>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  {PAY_METHODS.map(([k, l]) => (
                    <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: T.text2, cursor: 'pointer' }}>
                      <input type="radio" name="paymethod" checked={payMethod === k} onChange={() => setPayMethod(k)} style={{ accentColor: '#2563eb', width: 16, height: 16 }} />{l}
                    </label>
                  ))}
                </div>
                {!info.payment.online && (
                  <p style={{ margin: '10px 0 0', fontSize: 11.5, color: T.text3, lineHeight: 1.5 }}>Online payment is being set up. Placing the order saves it; our team will share payment details and activate your plan once paid.</p>
                )}
              </div>

              {formError && <p style={{ margin: 0, fontSize: 12.5, color: '#dc2626' }}>{formError}</p>}
            </div>
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
          {placed ? (
            <button onClick={onDone} style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Done</button>
          ) : (
            <button onClick={pay} disabled={!quote || !!quoteError || quoting || busy}
              style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: (!quote || quoteError || quoting || busy) ? 0.55 : 1 }}>
              {busy ? 'Placing order…' : info && !info.payment.online ? `Place Order${quote ? ` · ${inr(quote.total)}` : ''}` : `Make Payment${quote ? ` · ${inr(quote.total)}` : ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
